import { Building, MapPin, Users } from "lucide-react";
import type { ToolWidgetProps } from "./index";
import { ProfileCardActions } from "./profile-card-actions";

interface Address {
  postal_code?: string;
  locality?: string;
  formatted_address?: string;
  latitude?: number;
  longitude?: number;
  administrative_area_level_1?: string;
  country?: string;
}

interface Ville {
  id: number;
  name: string;
  user_id?: number;
  inhabitants?: number;
  chalandise?: number;
  point_of_sales_count?: number;
  installation_aids_count?: number;
  secteurs?: string[];
  parent_secteurs?: string[];
  address?: Address;
}

interface SearchVillesData {
  results: Ville[];
  total: number;
}

function VilleCard({ ville }: { ville: Ville }) {
  return (
    <div className="flex flex-col gap-2 p-3 bg-white rounded-lg border border-slate-200 hover:border-slate-300 transition-colors relative">
      <ProfileCardActions
        profile={{
          type: "ville",
          id: ville.id,
          name: ville.name,
          user_id: ville.user_id,
        }}
      />
          <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center">
            <Building className="w-5 h-5 text-slate-400" />
          </div>
          <div className="min-w-0">
            <div className="font-medium text-slate-800 truncate">
              {ville.name}
            </div>
            {ville.address?.administrative_area_level_1 && (
              <div className="text-xs text-slate-500 truncate">{ville.address.administrative_area_level_1}</div>
            )}
            <div className="flex flex-col gap-1 mt-2 text-xs text-slate-500">
              {ville.inhabitants && (
                <div className="flex items-center gap-1">
                  <Users className="w-3 h-3" />
                  {ville.inhabitants.toLocaleString("fr-FR")} hab.
                </div>
              )}
              {ville.point_of_sales_count !== undefined && ville.point_of_sales_count > 0 && (
                <div className="flex items-center gap-1">
                  <MapPin className="w-3 h-3" />
                  {ville.point_of_sales_count} {ville.point_of_sales_count === 1 ? 'point de vente' : 'points de vente'}
                </div>
              )}
            </div>
          </div>
    </div>
  );
}

export function SearchVillesWidget({
  data,
  count,
}: ToolWidgetProps<SearchVillesData>) {
  const { results = [] } = data || {};

  if (!results.length) {
    return (
      <div className="text-sm text-slate-500 italic">Aucune ville trouvée</div>
    );
  }

  return (
    <div className="grid grid-cols-4 gap-3">
      {results.map((ville) => (
        <VilleCard key={ville.id} ville={ville} />
      ))}
      {count && count > results.length && (
        <div className="col-span-4 text-xs text-slate-400 text-center pt-1">
          + {count - results.length} autres résultats
        </div>
      )}
    </div>
  );
}
